// src/hooks/useWebSocket.js
import { useEffect, useState } from 'react';
import { WS_URL } from '../config';

let sharedSocket = null;
let listeners = new Set();
let closeTimeout = null;

/**
 * Creates a WebSocket connection with reconnect logic and shared message handling.
 */
function createSocket(url) {
  const socket = new WebSocket(WS_URL);

  socket.onopen = () => {
    console.log('📡 Shared WebSocket connected');
  };

  socket.onclose = () => {
    console.log('🔌 Shared WebSocket disconnected, attempting to reconnect...');
    setTimeout(() => {
      sharedSocket = createSocket(WS_URL);
    }, 1000); // Try to reconnect after 1 second
  };

  socket.onerror = (err) => {
    console.error('⚠️ Shared WebSocket error:', err);
  };

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      listeners.forEach((listener) => listener(data));
    } catch {
      console.warn('🔧 Could not parse message:', event.data);
    }
  };

  return socket;
}

/**
 * Send a message to the WebSocket server, if connected.
 */
export function sendWebSocketMessage(message) {
  if (sharedSocket && sharedSocket.readyState === WebSocket.OPEN) {
    sharedSocket.send(JSON.stringify(message));
  } else {
    console.warn('❌ WebSocket not ready, could not send message:', message);
  }
}

/**
 * React hook to subscribe to the shared WebSocket.
 */
export function useWebSocket(url) {
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    if (!sharedSocket) {
      sharedSocket = createSocket(url);
    }

    function handleMessage(data) {
      setLastMessage(data);
    }

    listeners.add(handleMessage);

    return () => {
      listeners.delete(handleMessage);

      if (listeners.size === 0) {
        closeTimeout = setTimeout(() => {
          if (listeners.size === 0 && sharedSocket) {
            console.log('🛑 Closing unused WebSocket connection');
            sharedSocket.close();
            sharedSocket = null;
          }
        }, 1000);
      }
    };
  }, [url]);

  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, []);

  return lastMessage;
}
